import { useMemo } from 'react';
import ContactForm from './components/ContactForm';
import NewsletterSignup from './components/NewsletterSignup';
import JsonLd from '../../components/base/JsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://workplaysave.com';
const DEFAULT_OG_IMAGE = 'https://public.readdy.ai/ai/img_res/7965406aa664502cb6a3888418e2a788.jpg';

export default function AboutPage() {
  const aboutPageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Work Play Save | Tech Reviews & Deals',
    description: 'Learn about Work Play Save and our mission to help you find the best tech for gaming, streaming, and productivity through honest reviews and curated deals.',
    url: `${SITE_URL}/about`,
    publisher: {
      '@type': 'Organization',
      name: 'Work Play Save',
      url: SITE_URL,
      description: 'Curated tech reviews and exclusive affiliate deals for gamers, streamers, and productivity enthusiasts.',
    },
  }), []);

  return (
    <>
      <title>About Work Play Save | Tech Reviews &amp; Deals</title>
      <meta name="description" content="Learn about Work Play Save and our mission to help you find the best tech for gaming, streaming, and productivity through honest reviews and curated deals." />
      <meta name="keywords" content="about work play save, tech review site, gaming tech reviews, streaming gear reviews, productivity tech reviews, affiliate tech site, honest tech reviews" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${SITE_URL}/about`} />
      <meta property="og:title" content="About Work Play Save | Tech Reviews &amp; Deals" />
      <meta property="og:description" content="Learn about Work Play Save and our mission to help you find the best tech for gaming, streaming, and productivity through honest reviews and curated deals." />
      <meta property="og:url" content={`${SITE_URL}/about`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:site_name" content="Work Play Save" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="About Work Play Save | Tech Reviews &amp; Deals" />
      <meta name="twitter:description" content="Learn about Work Play Save and our mission to help you find the best tech for gaming, streaming, and productivity through honest reviews and curated deals." />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      <JsonLd data={aboutPageSchema} />
      <div className="min-h-screen">
        {/* Page Hero */}
        <div className="relative bg-transparent border-b border-gray-800">
          <div className="max-w-4xl mx-auto px-6 py-12 relative z-10">
            <div className="text-center space-y-4">
              <h1 className="text-5xl font-bold text-white uppercase tracking-tight">
                About Work Play Save
                <div className="h-1 w-32 bg-gradient-to-r from-transparent via-cyan-500 to-transparent mx-auto mt-4"></div>
              </h1>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-12 relative">
          <div className="relative z-10 space-y-8 text-gray-300 leading-relaxed">
            <section className="space-y-4">
              <p className="text-lg">
                Work Play Save is a technology discovery site focused on helping readers find the best tools for gaming, streaming, and productivity.
              </p>
              <p className="text-lg">
                Our goal is simple: highlight technology that improves your setup without overspending.
              </p>
              <p className="text-lg">
                We research, compare, and review products across categories including gaming peripherals, streaming equipment, and office productivity tools.
              </p>
              <p className="text-lg">
                Our recommendations are based on product research, user feedback, and real-world performance comparisons.
              </p>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">Affiliate Disclosure</h2>
              <p className="text-lg">
                Some links on this site are affiliate links, which means we may earn a commission if you purchase through them. This comes at no extra cost to you and helps support the site so we can continue publishing reviews and buying guides.
              </p>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">Our Focus Areas</h2>
              <div className="grid md:grid-cols-3 gap-6 mt-6">
                <div className="bg-gray-900/30 backdrop-blur-sm border border-white/10 rounded-xl p-6 space-y-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-purple-600 rounded-lg">
                    <i className="ri-gamepad-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-white">Gaming Tech</h3>
                  <p className="text-gray-400">
                    Keyboards, mice, monitors, and accessories for competitive and casual gaming.
                  </p>
                </div>
                <div className="bg-gray-900/30 backdrop-blur-sm border border-white/10 rounded-xl p-6 space-y-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-cyan-600 rounded-lg">
                    <i className="ri-live-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-white">Streaming Equipment</h3>
                  <p className="text-gray-400">
                    Microphones, webcams, lighting, and tools for content creators.
                  </p>
                </div>
                <div className="bg-gray-900/30 backdrop-blur-sm border border-white/10 rounded-xl p-6 space-y-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-600 rounded-lg">
                    <i className="ri-briefcase-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-white">Office Productivity</h3>
                  <p className="text-gray-400">
                    Monitors, chairs, keyboards, and workspace essentials for remote work.
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Divider before newsletter */}
        <div className="max-w-4xl mx-auto px-6">
          <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>
        </div>

        {/* Newsletter Signup Section */}
        <NewsletterSignup />

        {/* Divider before contact */}
        <div className="max-w-4xl mx-auto px-6">
          <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>
        </div>

        {/* Contact Us Section */}
        <ContactForm />
      </div>
    </>
  );
}
